import { useState, useEffect, useRef } from "react"
import { Button } from "../ui/button"
import { Card } from "../ui/card"
import { Phone, PhoneOff, AlertCircle } from "lucide-react"
import { cn } from "../../lib/utils"
import { useCallApi } from "../../hooks/useCallApi"

interface CallPopupProps {
  callerName: string
  callerPhone: string
  isVisible: boolean
  onHangUp: () => void
  onCallError?: (error: string) => void
}

function CallPopup({ callerName, callerPhone, isVisible, onHangUp, onCallError }: CallPopupProps) {
  const callApi = useCallApi()
  const [callDuration, setCallDuration] = useState(0)
  const [callState, setCallState] = useState<'idle' | 'connecting' | 'calling' | 'connected' | 'disconnecting' | 'reconnecting' | 'ended'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [callSid, setCallSid] = useState<string | null>(null)
  const [isReady, setIsReady] = useState(false)
  
  const isCallActiveRef = useRef(false)
  const eventListenersSetupRef = useRef(false)
  const connectedAtRef = useRef<number | null>(null)
  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Reset all state when popup closes
  useEffect(() => {
    if (!isVisible) {
      // Clear any pending auto-close timeout
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current)
        autoCloseTimeoutRef.current = null
      }
      
      setCallDuration(0)
      setCallState('idle')
      setError(null)
      setCallSid(null)
      setIsReady(false)
      isCallActiveRef.current = false
      eventListenersSetupRef.current = false
      connectedAtRef.current = null
    }
  }, [isVisible])

  // Set up event listeners only when popup is visible
  useEffect(() => {
    if (!isVisible || eventListenersSetupRef.current) return

    eventListenersSetupRef.current = true

    const handleConnecting = () => {
      if (!isVisible) return
      isCallActiveRef.current = true
      setCallState('connecting')
      setError(null)
    }

    const handleCalling = () => {
      if (!isVisible) return
      setCallState('calling')
    }

    const handleConnected = (sid: string) => {
      if (!isVisible || !isCallActiveRef.current) return
      connectedAtRef.current = Date.now()
      setCallState('connected')
      setCallSid(sid)
      setError(null)
    }

    const handleDisconnected = () => {
      if (!isVisible) return
      isCallActiveRef.current = false
      connectedAtRef.current = null
      setCallState('ended')
      
      // Clear any existing timeout before setting a new one
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current)
      }
      
      // Auto-close after 2 seconds
      autoCloseTimeoutRef.current = setTimeout(() => {
        setCallState('idle')
        setCallSid(null)
        setError(null)
        onHangUp()
        autoCloseTimeoutRef.current = null
      }, 2000)
    }

    const handleError = (err: Error) => {
      if (!isVisible) return
      isCallActiveRef.current = false
      connectedAtRef.current = null
      setCallState('idle')
      setCallSid(null)
      setError(err.message)
      onCallError?.(err.message)
    }

    callApi.on('connecting', handleConnecting)
    callApi.on('calling', handleCalling)
    callApi.on('connected', handleConnected)
    callApi.on('disconnected', handleDisconnected)
    callApi.on('error', handleError)

    return () => {
      callApi.off('connecting', handleConnecting)
      callApi.off('calling', handleCalling)
      callApi.off('connected', handleConnected)
      callApi.off('disconnected', handleDisconnected)
      callApi.off('error', handleError)
      eventListenersSetupRef.current = false
    }
  }, [isVisible, callApi, onHangUp, onCallError])

  // Note: Call is initiated by parent component (Leads.tsx or LeadDetails.tsx)
  // This popup only displays the UI and listens to call events

  // Timer - only count actual connected time
  useEffect(() => {
    if (callState !== 'connected' || !connectedAtRef.current) {
      setCallDuration(0)
      return
    }

    const interval = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - connectedAtRef.current!) / 1000))
    }, 1000)

    return () => clearInterval(interval)
  }, [callState])

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
    }
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  const handleHangUpClick = async () => {
    try {
      isCallActiveRef.current = false
      setCallState('disconnecting')
      await callApi.hangUp()
      // Don't call onHangUp() here - let the 'disconnected' event handler do it
      // This prevents double-calling onHangUp and race conditions
    } catch (error) {
      console.error('Failed to hang up:', error)
      // On error, clean up and close immediately
      setCallState('idle')
      setCallSid(null)
      setError(null)
      connectedAtRef.current = null
      onHangUp()
    }
  }

  const getStatusMessage = () => {
    if (error) return null
    
    switch (callState) {
      case 'idle':
        return isReady ? null : 'Initializing device...'
      case 'connecting':
        return 'Calling...'
      case 'calling':
        return 'Ringing...'
      case 'connected':
        return 'Connected'
      case 'disconnecting':
        return 'Hanging up...'
      case 'reconnecting':
        return 'Reconnecting...'
      case 'ended':
        return 'Call Ended'
      default:
        return null
    }
  }

  const getStatusColor = () => {
    if (error) return 'text-red-600'
    
    switch (callState) {
      case 'connected':
        return 'text-green-600'
      case 'disconnecting':
        return 'text-orange-600'
      case 'reconnecting':
        return 'text-orange-600'
      case 'ended':
        return 'text-red-600'
      case 'idle':
        return isReady ? null : 'text-slate-500'
      default:
        return 'text-slate-500'
    }
  }

  const isDisabled = callState === 'disconnecting' || callState === 'connecting' || (!isReady && callState === 'idle') || callState === 'ended'

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-sm bg-white shadow-2xl border-slate-200 animate-in fade-in-0 zoom-in-95 duration-300">
        <div className="p-8 text-center space-y-6">
          {/* Caller Icon */}
          <div className={cn(
            "mx-auto w-20 h-20 rounded-full flex items-center justify-center transition-all",
            callState === 'connected' ? 'bg-green-100' : 
            callState === 'ended' ? 'bg-red-100' : 'bg-blue-100'
          )}>
            <Phone className={cn(
              "w-8 h-8 transition-colors",
              callState === 'connected' ? 'text-green-600' : 
              callState === 'ended' ? 'text-red-600' : 'text-blue-600'
            )} />
          </div>

          {/* Caller Info */}
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-slate-900">{callerName}</h2>
            <p className="text-sm text-slate-600">{callerPhone}</p>
          </div>

          {/* Status Message */}
          {getStatusMessage() && (
            <p className={cn("text-sm font-medium transition-colors", getStatusColor())}>
              {getStatusMessage()}
            </p>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 text-left">{error}</p>
            </div>
          )}

          {/* Call Duration */}
          {callState === 'connected' && (
            <div className="space-y-2">
              <div className="text-4xl font-mono font-bold text-slate-900 tracking-tight">
                {formatTime(callDuration)}
              </div>
              {callSid && (
                <p className="text-xs text-slate-400">
                  Call ID: {callSid.slice(-8)}
                </p>
              )}
            </div>
          )}

          {/* Loading State */}
          {(callState === 'connecting' || callState === 'calling' || callState === 'reconnecting' || (!isReady && callState === 'idle')) && (
            <div className="flex justify-center py-2">
              <div className="w-6 h-6 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
          )}

          {/* Hang Up Button */}
          <div className="pt-2 flex justify-center">
            <Button
              onClick={handleHangUpClick}
              disabled={isDisabled}
              className={cn(
                "w-16 h-16 rounded-full shadow-lg",
                "transition-all duration-200 bg-red-600 hover:bg-red-700",
                "border-0 flex items-center justify-center p-0",
                "text-white disabled:bg-slate-300 disabled:cursor-not-allowed",
                "active:scale-95"
              )}
              aria-label="End call"
            >
              <PhoneOff className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default CallPopup